import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  IconButton,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Timeline as TimelineIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
} from '@mui/icons-material';
import {
  selectTimelines,
  addTimeline,
  removeTimeline,
  updateTimeline,
  setTimelines,
} from './timelinesSlice';
import { getTimelineEditorUrl } from '../../urls';
import useThemeStyles from '../../hooks/useThemeStyles';
import timelineService from '../../utils/apiRequests/timelineService';

/**
 * Generate a unique ID
 */
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

/**
 * Normalize backend timeline data to frontend format
 * Backend uses _id, frontend uses id
 * Backend sends Date objects as ISO strings in JSON, convert to epoch timestamps
 */
const normalizeTimeline = (backendTimeline) => {
  if (!backendTimeline) return null;

  const normalized = {
    ...backendTimeline,
    id: backendTimeline.id || backendTimeline._id?.toString() || backendTimeline._id,
    projectId: backendTimeline.projectId?.toString() || backendTimeline.projectId,
  };

  // Convert Date strings to epoch timestamps
  if (backendTimeline.createdAt) {
    if (typeof backendTimeline.createdAt === 'string') {
      normalized.createdAt = new Date(backendTimeline.createdAt).getTime();
    } else if (backendTimeline.createdAt instanceof Date) {
      normalized.createdAt = backendTimeline.createdAt.getTime();
    } else {
      normalized.createdAt = backendTimeline.createdAt;
    }
  } else {
    normalized.createdAt = Date.now();
  }

  if (backendTimeline.updatedAt) {
    if (typeof backendTimeline.updatedAt === 'string') {
      normalized.updatedAt = new Date(backendTimeline.updatedAt).getTime();
    } else if (backendTimeline.updatedAt instanceof Date) {
      normalized.updatedAt = backendTimeline.updatedAt.getTime();
    } else {
      normalized.updatedAt = backendTimeline.updatedAt;
    }
  } else {
    normalized.updatedAt = Date.now();
  }

  return normalized;
};

const TimelinesListPage = () => {
  const { colors, isDark } = useThemeStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { projectId } = useParams();

  const allTimelines = useSelector(selectTimelines);
  const timelines = useMemo(
    () => allTimelines.filter((tl) => tl.projectId === projectId),
    [allTimelines, projectId]
  );
  const activeProject = useSelector((state) => state.projects.activeProject);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newTimelineName, setNewTimelineName] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [timelineToDelete, setTimelineToDelete] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [timelineToEdit, setTimelineToEdit] = useState(null);
  const [editName, setEditName] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuTimelineId, setMenuTimelineId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [updating, setUpdating] = useState(false);

  // Load timelines from backend on mount
  useEffect(() => {
    const loadTimelines = async () => {
      if (!projectId) return;

      setLoading(true);
      try {
        const response = await timelineService.getTimelines(projectId);

        if (response.error) {
          console.error('Error loading timelines:', response.error);
          // If error, keep existing state (might be from persistence)
          setLoading(false);
          return;
        }

        if (response.data?.timelines) {
          const normalizedTimelines = response.data.timelines.map(normalizeTimeline);
          dispatch(setTimelines(normalizedTimelines));
        }
      } catch (err) {
        console.error('Error loading timelines:', err);
      } finally {
        setLoading(false);
      }
    };

    loadTimelines();
  }, [projectId, dispatch]);

  const handleCreateTimeline = useCallback(async () => {
    if (!newTimelineName.trim() || creating || !projectId) return;

    setCreating(true);
    try {
      // Get chapters from the active project
      const chapters = activeProject?.thinChapters || [];

      // Default chapter width
      const DEFAULT_CHAPTER_WIDTH = 400;

      // Create chapter borders from manuscript chapters
      // Each chapter starts where the previous one ends
      const chapterBorders = chapters.map((chapter, index) => ({
        id: generateId(),
        x: index * DEFAULT_CHAPTER_WIDTH,
        width: DEFAULT_CHAPTER_WIDTH,
        label: chapter.name || chapter.title || `Chapter ${index + 1}`,
      }));

      const newTimelineData = {
        name: newTimelineName.trim(),
        chapterBorders,
        notes: [],
        viewState: {
          panX: 0,
          panY: 0,
          zoom: 1,
        },
        showChapterBorders: true,
      };

      const response = await timelineService.createTimeline(projectId, newTimelineData);

      if (response.error) {
        console.error('Error creating timeline:', response.error);
        return;
      }

      if (response.data?.data) {
        const normalizedTimeline = normalizeTimeline(response.data.data);
        dispatch(addTimeline(normalizedTimeline));
        setNewTimelineName('');
        setCreateDialogOpen(false);
        // Navigate to the new timeline
        navigate(getTimelineEditorUrl(projectId, normalizedTimeline.id));
      }
    } catch (err) {
      console.error('Error creating timeline:', err);
    } finally {
      setCreating(false);
    }
  }, [dispatch, projectId, newTimelineName, activeProject, creating, navigate]);

  const handleDeleteTimeline = useCallback(async () => {
    if (!timelineToDelete || deleting || !projectId) return;

    setDeleting(true);
    try {
      const response = await timelineService.deleteTimeline(projectId, timelineToDelete);

      if (response.error) {
        console.error('Error deleting timeline:', response.error);
        return;
      }

      // Remove from Redux store on success
      dispatch(removeTimeline(timelineToDelete));
      setTimelineToDelete(null);
      setDeleteDialogOpen(false);
    } catch (err) {
      console.error('Error deleting timeline:', err);
    } finally {
      setDeleting(false);
    }
  }, [dispatch, timelineToDelete, deleting, projectId]);

  const handleEditTimeline = useCallback(async () => {
    if (!timelineToEdit || !editName.trim() || updating || !projectId) return;

    setUpdating(true);
    try {
      const response = await timelineService.updateTimeline(projectId, timelineToEdit, {
        name: editName.trim(),
      });

      if (response.error) {
        console.error('Error updating timeline:', response.error);
        return;
      }

      if (response.data?.data) {
        const normalizedTimeline = normalizeTimeline(response.data.data);
        dispatch(updateTimeline(normalizedTimeline));
        setTimelineToEdit(null);
        setEditName('');
        setEditDialogOpen(false);
      }
    } catch (err) {
      console.error('Error updating timeline:', err);
    } finally {
      setUpdating(false);
    }
  }, [dispatch, timelineToEdit, editName, updating, projectId]);

  const handleDuplicateTimeline = useCallback(
    async (timelineId) => {
      if (creating || !projectId) return;

      const timeline = timelines.find((t) => t.id === timelineId);
      if (!timeline) return;

      setCreating(true);
      try {
        // Generate new IDs for notes and chapter borders
        const duplicatedTimelineData = {
          name: `${timeline.name} (Copy)`,
          notes:
            timeline.notes?.map((note) => {
              // If note is a populated object, use its _id, otherwise use the ID string
              const noteId = note._id || note.id || note;
              return typeof noteId === 'string' ? noteId : noteId.toString();
            }) || [],
          chapterBorders:
            timeline.chapterBorders?.map((border) => ({
              ...border,
              id: generateId(),
            })) || [],
          viewState: timeline.viewState || {
            panX: 0,
            panY: 0,
            zoom: 1,
          },
          showChapterBorders: timeline.showChapterBorders ?? true,
        };

        const response = await timelineService.createTimeline(projectId, duplicatedTimelineData);

        if (response.error) {
          console.error('Error duplicating timeline:', response.error);
          return;
        }

        if (response.data?.data) {
          const normalizedTimeline = normalizeTimeline(response.data.data);
          dispatch(addTimeline(normalizedTimeline));
        }
      } catch (err) {
        console.error('Error duplicating timeline:', err);
      } finally {
        setCreating(false);
        setMenuAnchor(null);
        setMenuTimelineId(null);
      }
    },
    [dispatch, projectId, timelines, creating]
  );

  const handleOpenTimeline = useCallback(
    (timelineId) => {
      navigate(getTimelineEditorUrl(projectId, timelineId));
    },
    [navigate, projectId]
  );

  const handleMenuClick = (event, timelineId) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuTimelineId(timelineId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuTimelineId(null);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          flexGrow: 1,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: colors.background.primary,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'auto',
        p: 3,
        backgroundColor: colors.background.primary,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Timelines
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Organize your story events and notes on visual timelines
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={creating ? <CircularProgress size={20} /> : <AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          disabled={creating}
        >
          New Timeline
        </Button>
      </Box>

      {/* Timelines Grid */}
      {timelines.length > 0 ? (
        <Grid container spacing={3}>
          {timelines.map((timeline) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={timeline.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  },
                }}
              >
                <CardActionArea
                  onClick={() => handleOpenTimeline(timeline.id)}
                  sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 2 }}>
                    {/* Preview */}
                    <Box
                      sx={{
                        height: 100,
                        mb: 2,
                        borderRadius: 1,
                        backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        position: 'relative',
                      }}
                    >
                      {/* Simplified timeline preview */}
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 16,
                          right: 16,
                          height: 2,
                          backgroundColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                        }}
                      />
                      {/* Mini notes preview */}
                      {timeline.notes.slice(0, 5).map((note, idx) => (
                        <Box
                          key={note._id || note.id || idx}
                          sx={{
                            position: 'absolute',
                            left: `${15 + idx * 15}%`,
                            top: '30%',
                            width: 24,
                            height: 16,
                            backgroundColor: isDark ? 'rgba(254, 243, 199, 0.8)' : '#fef3c7',
                            borderRadius: 0.5,
                            boxShadow: 1,
                          }}
                        />
                      ))}
                      {/* Chapter border previews */}
                      {timeline.showChapterBorders &&
                        timeline.chapterBorders.slice(0, 3).map((border, idx) => (
                          <Box
                            key={border.id || idx}
                            sx={{
                              position: 'absolute',
                              left: `${30 + idx * 25}%`,
                              top: 0,
                              bottom: 0,
                              width: 1,
                              borderLeft: `1px dotted ${isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'}`,
                            }}
                          />
                        ))}
                      <TimelineIcon
                        sx={{
                          fontSize: 32,
                          color: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)',
                          zIndex: 0,
                        }}
                      />
                    </Box>

                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {timeline.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {timeline.notes.length} notes · {timeline.chapterBorders.length} chapters
                    </Typography>
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      Updated {formatDate(timeline.updatedAt)}
                    </Typography>
                  </CardContent>
                </CardActionArea>

                {/* Card Actions */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    px: 1,
                    py: 0.5,
                    borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuClick(e, timeline.id)}
                    sx={{ color: isDark ? 'rgba(255,255,255,0.6)' : 'inherit' }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 300,
            textAlign: 'center',
            color: 'text.secondary',
          }}
        >
          <TimelineIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
          <Typography variant="h6" gutterBottom>
            No timelines yet
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Create your first timeline to visualize story events and organize notes chronologically.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Timeline
          </Button>
        </Box>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            const timeline = timelines.find((t) => t.id === menuTimelineId);
            setTimelineToEdit(menuTimelineId);
            setEditName(timeline?.name || '');
            setEditDialogOpen(true);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleDuplicateTimeline(menuTimelineId)}>
          <ListItemIcon>
            <DuplicateIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setTimelineToDelete(menuTimelineId);
            setDeleteDialogOpen(true);
            handleMenuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Create New Timeline</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Timeline Name"
            value={newTimelineName}
            onChange={(e) => setNewTimelineName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateTimeline();
            }}
            placeholder="Enter timeline name..."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateTimeline}
            variant="contained"
            disabled={!newTimelineName.trim() || creating}
          >
            {creating ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Rename Timeline</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Timeline Name"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleEditTimeline();
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleEditTimeline}
            variant="contained"
            disabled={!editName.trim() || updating}
          >
            {updating ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Timeline?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this timeline? This action cannot be undone and all
            notes within this timeline will be permanently deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteTimeline}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TimelinesListPage;
